import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  X, 
  Minimize2, 
  Maximize2, 
  Send, 
  Paperclip, 
  Image,
  Type,
  Code,
  AlertCircle,
  Check,
  Move3d
} from 'lucide-react';
import { Email, EmailAccount } from '../../types';
import { cn } from '../../utils';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { getApiBaseUrl } from '../../config/env';

// React Quill导入
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// 自定义工具栏配置
const toolbarOptions = [
  [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ 'color': [] }, { 'background': [] }],
  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
  [{ 'indent': '-1'}, { 'indent': '+1' }],
  [{ 'align': [] }],
  ['link', 'image'],
  ['clean']
];

// React Quill模块配置
const quillModules = {
  toolbar: toolbarOptions
};

export interface EmailSendData {
  from: string;
  to: string;
  subject: string;
  content: string;
  html_content?: string;
  reply_to_id?: number;
  account_id: number;
}

export interface EmailSendResult {
  taskId: string;
}

interface EmailComposerProps {
  isOpen: boolean;
  onClose: () => void;
  replyToEmail?: Email | null;
  onSend: (emailData: EmailSendData) => Promise<EmailSendResult>;
}

const EmailComposer: React.FC<EmailComposerProps> = ({
  isOpen,
  onClose,
  replyToEmail,
  onSend
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [isRichText, setIsRichText] = useState(true);
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number>(0);
  
  // 发送任务相关状态
  const [sendTaskId, setSendTaskId] = useState<string | null>(null);
  const [sendStatus, setSendStatus] = useState<string>('');
  const [sendMessage, setSendMessage] = useState<string>('');
  const statusCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // 附件相关状态
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // 拖拽调整大小相关状态
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(600);
  const composerRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef({
    isDragging: false,
    direction: '',
    startX: 0,
    startY: 0,
    startWidth: 0,
    startHeight: 0
  });

  // 获取可用的邮箱账户
  const fetchAccounts = useCallback(async () => {
    try {
      console.log('正在获取邮箱账户...');
      const response = await fetch(`${getApiBaseUrl()}/accounts`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('邮箱账户响应:', data);
      
      // 后端返回格式: {accounts: [], total: number}
      const accountsList = data.accounts || [];
      const activeAccounts = accountsList.filter((acc: EmailAccount) => acc.is_active);
      console.log('活跃账户:', activeAccounts);
      
      setAccounts(activeAccounts);
      
      // 只在没有选择账户且有可用账户时才设置默认账户
      if (activeAccounts.length > 0 && selectedAccountId === 0) {
        setSelectedAccountId(activeAccounts[0].id);
        setFrom(activeAccounts[0].email);
        console.log('选择默认账户:', activeAccounts[0].email);
      }
      
      return activeAccounts; // 返回账户列表
    } catch (error) {
      console.error('获取邮箱账户失败:', error);
      // 设置一个临时账户用于测试
      const testAccount = {
        id: 6, // 使用实际存在的账户ID
        email: 'service@digen.ai',
        is_active: true,
        imap_server: 'imap.gmail.com',
        imap_port: 993,
        smtp_server: 'smtp.gmail.com',
        smtp_port: 587,
        created_at: '',
        updated_at: ''
      };
      setAccounts([testAccount]);
      if (selectedAccountId === 0) {
        setSelectedAccountId(testAccount.id);
        setFrom(testAccount.email);
        console.log('使用测试账户:', testAccount.email);
      }
      
      return [testAccount]; // 返回测试账户
    }
  }, []); // 移除selectedAccountId依赖避免循环

  // 拖拽调整大小处理
  const handleResizeStart = (e: React.MouseEvent, direction: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 使用ref存储拖拽状态，避免闭包问题
    dragStateRef.current = {
      isDragging: true,
      direction,
      startX: e.clientX,
      startY: e.clientY,
      startWidth: width,
      startHeight: height
    };
    
    // 添加全局鼠标事件监听
    const handleMouseMove = (e: MouseEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState.isDragging) return;
      
      const deltaX = e.clientX - dragState.startX;
      const deltaY = e.clientY - dragState.startY;
      
      let newWidth = dragState.startWidth;
      let newHeight = dragState.startHeight;
      
      if (dragState.direction.includes('left')) {
        // 向左拖拽增加宽度
        newWidth = Math.max(400, dragState.startWidth - deltaX);
      }
      if (dragState.direction.includes('right')) {
        // 向右拖拽增加宽度
        newWidth = Math.max(400, dragState.startWidth + deltaX);
      }
      if (dragState.direction.includes('top')) {
        // 向上拖拽增加高度
        newHeight = Math.max(300, dragState.startHeight - deltaY);
      }
      if (dragState.direction.includes('bottom')) {
        // 向下拖拽增加高度
        newHeight = Math.max(300, dragState.startHeight + deltaY);
      }
      
      setWidth(newWidth);
      setHeight(newHeight);
    };
    
    const handleMouseUp = () => {
      dragStateRef.current.isDragging = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // 重置表单
  const resetForm = useCallback(() => {
    if (!replyToEmail) {
      setTo('');
      setSubject('');
    }
    setContent('');
    setHtmlContent('');
    setAttachments([]);
    setIsRichText(true);
  }, [replyToEmail]);

  // 初始化表单
  useEffect(() => {
    if (isOpen) {
      // 每次打开时都获取账户，确保数据最新
      fetchAccounts().then(() => {
        if (replyToEmail) {
          // 回复邮件模式
          const replyTo = replyToEmail.sender_email;
          const replySubject = replyToEmail.subject.startsWith('Re:') 
            ? replyToEmail.subject 
            : `Re: ${replyToEmail.subject}`;
          
          setTo(replyTo);
          setSubject(replySubject);
          
          // 设置引用原文
          // 处理时间格式
          const formatEmailTime = (dateString: string) => {
            let date: Date;
            if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
              date = new Date(dateString + 'Z');
            } else {
              date = new Date(dateString);
            }
            return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
          };

          const originalContent = `\n\n--- 原始邮件 ---\n发件人: ${replyToEmail.sender_email}\n时间: ${formatEmailTime(replyToEmail.received_at)}\n主题: ${replyToEmail.subject}\n\n${replyToEmail.content}`;
          setContent(originalContent);
          setHtmlContent(`<br><br><div style="border-left: 3px solid #ccc; padding-left: 10px; margin: 10px 0;"><strong>--- 原始邮件 ---</strong><br><strong>发件人:</strong> ${replyToEmail.sender_email}<br><strong>时间:</strong> ${formatEmailTime(replyToEmail.received_at)}<br><strong>主题:</strong> ${replyToEmail.subject}<br><br>${replyToEmail.html_content || replyToEmail.content.replace(/\n/g, '<br>')}</div>`);
        } else {
          resetForm();
        }
      });
    }
  }, [isOpen, replyToEmail, fetchAccounts, resetForm]);

  // 当账户列表更新时，自动选择匹配的账户
  useEffect(() => {
    if (accounts.length > 0 && replyToEmail) {
      // 自动选择对应的邮箱账户
      const matchingAccount = accounts.find(acc => 
        acc.email === replyToEmail.recipient_email
      );
      if (matchingAccount) {
        setSelectedAccountId(matchingAccount.id);
        setFrom(matchingAccount.email);
        console.log('回复邮件选择账户:', matchingAccount.email);
      }
    }
  }, [accounts, replyToEmail]);

  // 处理账户切换
  const handleAccountChange = (accountId: number) => {
    const account = accounts.find(acc => acc.id === accountId);
    if (account) {
      setSelectedAccountId(accountId);
      setFrom(account.email);
    }
  };

  // 处理文件附件
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setAttachments(prev => [...prev, ...files]);
  };

  // 处理图片上传并插入到编辑器
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        // 显示图片大小选择对话框
        const sizeChoice = window.prompt(
          '请选择图片大小：\n1 - 小图 (200px)\n2 - 中图 (400px)\n3 - 大图 (600px)\n4 - 原始大小\n\n请输入 1-4：',
          '2'
        );
        
        if (sizeChoice === null) return; // 用户取消了
        
        let maxWidth = '400px';
        let sizeLabel = '中图';
        switch (sizeChoice) {
          case '1':
            maxWidth = '200px';
            sizeLabel = '小图';
            break;
          case '2':
            maxWidth = '400px';
            sizeLabel = '中图';
            break;
          case '3':
            maxWidth = '600px';
            sizeLabel = '大图';
            break;
          case '4':
            maxWidth = '100%';
            sizeLabel = '原始大小';
            break;
          default:
            maxWidth = '400px';
            sizeLabel = '中图';
        }
        
        const reader = new FileReader();
        reader.onload = () => {
          // 插入图片到富文本编辑器，带有可调整样式和控制按钮
          const imageUrl = reader.result as string;
          const currentContent = htmlContent;
          const imageId = `img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          const imageHtml = `
            <div style="text-align: center; margin: 15px 0; padding: 10px; border: 1px solid #e5e7eb; border-radius: 8px; background: #f9fafb;">
              <div class="image-container" style="position: relative; display: inline-block;">
                <img id="${imageId}" src="${imageUrl}" alt="${file.name}" 
                     style="max-width: ${maxWidth}; height: auto; border: 2px solid transparent; border-radius: 4px; cursor: pointer;" 
                     title="点击选择，双击编辑大小" 
                     onclick="this.style.border = this.style.border === '2px solid rgb(59, 130, 246)' ? '2px solid transparent' : '2px solid #3b82f6';"
                     ondblclick="
                       const newSize = prompt('请选择新的图片大小：\\n1 - 小图 (200px)\\n2 - 中图 (400px)\\n3 - 大图 (600px)\\n4 - 原始大小\\n\\n请输入 1-4：', '2');
                       if (newSize) {
                         const sizes = {'1': '200px', '2': '400px', '3': '600px', '4': '100%'};
                         this.style.maxWidth = sizes[newSize] || '400px';
                       }
                     " />
              </div>
              <div style="font-size: 12px; color: #6b7280; margin-top: 8px; display: flex; justify-content: center; align-items: center; gap: 10px;">
                <span>📁 ${file.name}</span>
                <span>📏 ${sizeLabel}</span>
                <button onclick="this.parentElement.parentElement.remove();" 
                        style="background: #ef4444; color: white; border: none; padding: 2px 6px; border-radius: 3px; font-size: 10px; cursor: pointer;">
                  删除
                </button>
              </div>
            </div>
          `;
          setHtmlContent(currentContent + imageHtml);
        };
        reader.readAsDataURL(file);
      }
    });
    // 清空input值，允许重复选择同一文件
    event.target.value = '';
  };

  // 移除附件
  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // 处理发送
  const handleSend = async () => {
    if (!to.trim() || !subject.trim()) {
      alert('请填写收件人和主题');
      return;
    }

    if (!isRichText && !content.trim()) {
      alert('请填写邮件内容');
      return;
    }

    if (isRichText && !htmlContent.trim()) {
      alert('请填写邮件内容');
      return;
    }

    // 检查账户ID
    if (!selectedAccountId || selectedAccountId === 0) {
      alert('请选择发送账户');
      return;
    }

    console.log('发送邮件数据:', {
      selectedAccountId,
      from,
      to: to.trim(),
      subject: subject.trim(),
      accounts: accounts.length
    });

    setLoading(true);
          setSendStatus('sending');
    setSendMessage('正在创建发送任务...');

    try {
      // 确保content字段不为空
      let finalContent = '';
      if (isRichText) {
        // 从HTML内容中提取纯文本
        const extractedText = htmlContent.replace(/<[^>]*>/g, '').trim();
        // 如果提取的文本为空，使用HTML内容作为备选
        finalContent = extractedText || htmlContent.trim() || '邮件内容';
      } else {
        finalContent = content.trim() || '邮件内容';
      }

      const emailData: EmailSendData = {
        from,
        to: to.trim(),
        subject: subject.trim(),
        content: finalContent,
        html_content: isRichText ? htmlContent.trim() : undefined,
        reply_to_id: replyToEmail?.id,
        account_id: selectedAccountId
      };

      console.log('最终发送数据:', emailData);
      const result = await onSend(emailData);
      
      // 开始跟踪发送状态
      setSendTaskId(result.taskId);
      startStatusCheck(result.taskId);
      
    } catch (error) {
      console.error('发送邮件失败:', error);
      
      // 检查是否是重复发送错误
      if (error instanceof Error && error.message.includes('429')) {
        alert('检测到重复发送，请稍后再试');
      } else if (error instanceof Error && error.message.includes('检测到重复发送')) {
        alert('检测到重复发送，请稍后再试');
      } else {
        alert(error instanceof Error ? error.message : '发送邮件失败，请重试');
      }
      
      setLoading(false);
      setSendStatus('');
      setSendMessage('');
    }
  };

  // 开始状态检查
  const startStatusCheck = (taskId: string) => {
    // 清除之前的检查
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
    }

    const checkStatus = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/emails/send-status/${taskId}`);
        if (response.ok) {
          const status = await response.json();
          setSendStatus(status.status);
          setSendMessage(status.message);

          // 如果任务完成，停止检查
          if (['sent', 'failed', 'cancelled'].includes(status.status)) {
            if (statusCheckIntervalRef.current) {
              clearInterval(statusCheckIntervalRef.current);
              statusCheckIntervalRef.current = null;
            }
            
            setLoading(false);
            setSendTaskId(null);
            
            if (status.status === 'sent') {
              // 发送成功后重置表单并关闭
              resetForm();
              onClose();
              alert('邮件发送成功！');
            } else if (status.status === 'failed') {
              alert(`邮件发送失败: ${status.message}`);
            } else if (status.status === 'cancelled') {
              alert('邮件发送已取消');
            }
          }
        }
      } catch (error) {
        console.error('检查发送状态失败:', error);
      }
    };

    // 立即检查一次
    checkStatus();
    
    // 每2秒检查一次状态
    statusCheckIntervalRef.current = setInterval(checkStatus, 2000);
  };

  // 取消发送
  const handleCancelSend = async () => {
    if (!sendTaskId) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/emails/cancel-send/${sendTaskId}`, {
        method: 'POST'
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setSendStatus('cancelled');
          setSendMessage('发送已取消');
          setLoading(false);
          setSendTaskId(null);
          
          if (statusCheckIntervalRef.current) {
            clearInterval(statusCheckIntervalRef.current);
            statusCheckIntervalRef.current = null;
          }
        } else {
          alert(result.message);
        }
      }
    } catch (error) {
      console.error('取消发送失败:', error);
      alert('取消发送失败，请重试');
    }
  };

  // 清理状态检查
  useEffect(() => {
    return () => {
      if (statusCheckIntervalRef.current) {
        clearInterval(statusCheckIntervalRef.current);
      }
    };
  }, []);

  // 关闭时清理任务状态
  const handleClose = () => {
    if (statusCheckIntervalRef.current) {
      clearInterval(statusCheckIntervalRef.current);
      statusCheckIntervalRef.current = null;
    }
    
    setSendTaskId(null);
    setSendStatus('');
    setSendMessage('');
    setLoading(false);
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-0 right-6 z-50">
      <div 
        ref={composerRef}
        className={cn(
          'theme-bg-primary border theme-border-primary rounded-t-lg theme-shadow-lg theme-transition relative',
          isMinimized ? 'h-12' : ''
        )}
        style={{ 
          width: isMinimized ? '320px' : `${width}px`,
          height: isMinimized ? '48px' : `${height}px`
        }}
      >
        {/* 拖拽调整大小的控件 */}
        {!isMinimized && (
          <>
            {/* 左边缘 */}
            <div
              className="absolute top-0 left-0 w-2 h-full cursor-ew-resize bg-transparent hover:bg-blue-200 opacity-50 hover:opacity-100 transition-opacity z-10"
              onMouseDown={(e) => handleResizeStart(e, 'left')}
              title="拖拽调整宽度"
            />
            {/* 上边缘 */}
            <div
              className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize bg-transparent hover:bg-blue-200 opacity-50 hover:opacity-100 transition-opacity z-10"
              onMouseDown={(e) => handleResizeStart(e, 'top')}
              title="拖拽调整高度"
            />
            {/* 左上角 */}
            <div
              className="absolute top-0 left-0 w-4 h-4 cursor-nw-resize bg-transparent hover:bg-blue-200 opacity-50 hover:opacity-100 transition-opacity z-10"
              onMouseDown={(e) => handleResizeStart(e, 'top-left')}
              title="拖拽调整窗口大小"
            />
            {/* 拖拽指示器 */}
            <div className="absolute top-1 left-1 opacity-30 hover:opacity-60 transition-opacity pointer-events-none z-10">
              <Move3d className="w-3 h-3 text-gray-400" />
            </div>
          </>
        )}

        {/* 标题栏 */}
        <div className="flex items-center justify-between p-3 border-b theme-border-primary theme-bg-secondary rounded-t-lg">
          <div className="flex items-center space-x-2">
            <h3 className="font-medium theme-text-primary">
              {replyToEmail ? '回复邮件' : '撰写邮件'}
            </h3>
            {replyToEmail && (
              <Badge variant="info" size="sm">
                回复邮件
              </Badge>
            )}
          </div>
          
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1.5 theme-bg-hover rounded theme-transition"
              title={isMinimized ? '展开' : '最小化'}
            >
              {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 theme-bg-hover rounded theme-transition"
              title="关闭"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 内容区域 */}
        {!isMinimized && (
          <div className="flex flex-col h-[calc(100%-48px)]">
            {/* 表单区域 - 固定高度，可滚动 */}
            <div className="p-4 space-y-3 border-b theme-border-primary flex-shrink-0 max-h-64 overflow-y-auto">
              {/* 发件人选择 */}
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium theme-text-primary w-16 flex-shrink-0">
                  发件人:
                </label>
                <div className="flex-1">
                  {replyToEmail ? (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm theme-text-primary font-medium">{from}</span>
                      <Badge variant="info" size="sm">(回复邮件)</Badge>
                    </div>
                  ) : (
                    <select
                      value={selectedAccountId}
                      onChange={(e) => handleAccountChange(Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm border theme-border-primary rounded theme-input theme-transition"
                    >
                      {accounts.length === 0 ? (
                        <option value={0}>正在加载邮箱账户...</option>
                      ) : (
                        accounts.map(account => (
                          <option key={account.id} value={account.id}>
                            {account.email}
                          </option>
                        ))
                      )}
                    </select>
                  )}
                </div>
              </div>

              {/* 收件人 */}
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium theme-text-primary w-16 flex-shrink-0">
                  收件人:
                </label>
                <input
                  type="email"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border theme-border-primary rounded theme-input theme-transition"
                  placeholder="请输入收件人邮箱地址"
                  disabled={!!replyToEmail}
                />
              </div>

              {/* 主题 */}
              <div className="flex items-center space-x-3">
                <label className="text-sm font-medium theme-text-primary w-16 flex-shrink-0">
                  主题:
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border theme-border-primary rounded theme-input theme-transition"
                  placeholder="请输入邮件主题"
                />
              </div>

              {/* 编辑模式切换和工具栏 */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setIsRichText(!isRichText)}
                    className={cn(
                      'flex items-center space-x-1 px-3 py-1.5 text-xs rounded border theme-transition',
                      isRichText 
                        ? 'theme-blue-bg theme-blue-border theme-blue-text'
                        : 'theme-bg-secondary theme-border-primary theme-text-secondary'
                    )}
                  >
                    {isRichText ? <Type className="w-3 h-3" /> : <Code className="w-3 h-3" />}
                    <span>{isRichText ? '富文本' : '纯文本'}</span>
                  </button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center space-x-1 px-2 py-1 text-xs theme-text-secondary hover:theme-text-primary theme-bg-hover rounded theme-transition"
                    title="添加附件"
                  >
                    <Paperclip className="w-3 h-3" />
                    <span>附件</span>
                  </button>
                  
                  {isRichText && (
                    <button
                      onClick={() => imageInputRef.current?.click()}
                      className="flex items-center space-x-1 px-2 py-1 text-xs theme-text-secondary hover:theme-text-primary theme-bg-hover rounded theme-transition"
                      title="插入图片"
                    >
                      <Image className="w-3 h-3" />
                      <span>图片</span>
                    </button>
                  )}
                </div>
              </div>

              {/* 附件列表 */}
              {attachments.length > 0 && (
                <div className="space-y-1">
                  <label className="text-xs font-medium theme-text-primary">附件:</label>
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((file, index) => (
                      <div key={index} className="flex items-center space-x-1 px-2 py-1 theme-bg-secondary rounded text-xs">
                        <Paperclip className="w-3 h-3" />
                        <span>{file.name}</span>
                        <button
                          onClick={() => removeAttachment(index)}
                          className="theme-red-text hover:text-red-700 theme-transition"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 内容编辑区域 - 可滚动 */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {isRichText ? (
                <div className="flex-1 overflow-hidden">
                  <style>{`
                    .ql-editor img {
                      cursor: pointer !important;
                      transition: all 0.2s ease;
                      border: 2px solid transparent !important;
                    }
                    .ql-editor img:hover {
                      border-color: #3b82f6 !important;
                      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
                    }
                    .ql-editor img:focus {
                      outline: none;
                      border-color: #1d4ed8 !important;
                      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
                    }
                    .ql-editor .image-container {
                      position: relative;
                      display: inline-block;
                    }
                    .ql-editor .image-container:hover .image-controls {
                      opacity: 1;
                    }
                    .image-controls {
                      position: absolute;
                      top: 5px;
                      right: 5px;
                      opacity: 0;
                      transition: opacity 0.2s ease;
                      background: rgba(0, 0, 0, 0.7);
                      border-radius: 4px;
                      padding: 2px;
                    }
                    .image-controls button {
                      background: none;
                      border: none;
                      color: white;
                      font-size: 12px;
                      padding: 2px 4px;
                      cursor: pointer;
                      border-radius: 2px;
                    }
                    .image-controls button:hover {
                      background: rgba(255, 255, 255, 0.2);
                    }
                  `}</style>
                  <ReactQuill
                    value={htmlContent}
                    onChange={setHtmlContent}
                    modules={quillModules}
                    formats={[
                      'header', 'bold', 'italic', 'underline', 'strike',
                      'color', 'background', 'list', 'bullet', 'indent',
                      'link', 'image', 'align'
                    ]}
                    style={{ height: '100%' }}
                    className="h-full flex flex-col"
                    placeholder="请输入邮件内容..."
                  />
                </div>
              ) : (
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="flex-1 p-4 text-sm border-0 resize-none focus:ring-0 focus:outline-none overflow-y-auto theme-bg-primary theme-text-primary"
                  placeholder="请输入邮件内容..."
                  style={{ minHeight: '200px' }}
                />
              )}
            </div>

            {/* 底部工具栏 */}
            <div className="flex items-center justify-between p-4 border-t theme-border-primary theme-bg-secondary">
              <div className="flex items-center space-x-2 text-xs theme-text-tertiary">
                {accounts.length === 0 && (
                  <div className="flex items-center space-x-1 theme-yellow-text">
                    <AlertCircle className="w-3 h-3" />
                    <span>正在加载邮箱账户...</span>
                  </div>
                )}
                {replyToEmail && (
                  <div className="flex items-center space-x-1">
                    <Check className="w-3 h-3 theme-green-text" />
                    <span>回复模式</span>
                  </div>
                )}
                {/* 发送状态显示 */}
                {sendStatus && (
                  <div className="flex items-center space-x-1">
                    {sendStatus === 'sending' && <AlertCircle className="w-3 h-3 theme-blue-text animate-pulse" />}
                    {sendStatus === 'sending' && <AlertCircle className="w-3 h-3 theme-yellow-text animate-pulse" />}
                    {sendStatus === 'sent' && <Check className="w-3 h-3 theme-green-text" />}
                    {sendStatus === 'failed' && <AlertCircle className="w-3 h-3 theme-red-text" />}
                    {sendStatus === 'cancelled' && <X className="w-3 h-3 theme-text-secondary" />}
                    <span className={
                      sendStatus === 'sent' ? 'theme-green-text' :
                      sendStatus === 'failed' ? 'theme-red-text' :
                      sendStatus === 'cancelled' ? 'theme-text-secondary' :
                      'theme-blue-text'
                    }>
                      {sendMessage}
                    </span>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-2">
                {/* 如果正在发送，显示取消按钮 */}
                {loading && sendTaskId && (
                  <Button variant="ghost" onClick={handleCancelSend}>
                    取消发送
                  </Button>
                )}
                {/* 如果没有在发送，显示普通取消按钮 */}
                {!loading && (
                  <Button variant="ghost" onClick={handleClose}>
                    取消
                  </Button>
                )}
                <Button 
                  onClick={handleSend} 
                  loading={loading}
                  disabled={accounts.length === 0 || selectedAccountId === 0 || loading}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? '发送中...' : '发送'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFileUpload}
      />
      
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleImageUpload}
      />
    </div>
  );
};

export default EmailComposer;